import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  Paper,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Avatar
} from '@mui/material';
import { EDUGATE_API_URL } from '../../config/serviceConfig';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Star as StarIcon,
  School as SchoolIcon,
  Badge as BadgeIcon,
  Phone as PhoneIcon,
  Class as ClassIcon,
  AccountBox as AccountBoxIcon
} from '@mui/icons-material';

const TeamMembersForm = ({ data, onChange, editMode = false }) => {
  const { t } = useTranslation();
  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);

  // 获取学校列表
  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    setLoadingSchools(true);
    try {
      const response = await fetch(`${EDUGATE_API_URL}/api/organizations/schools`);
      const result = await response.json();
      if (result.code === 0) {
        setSchools(result.data.schools || []);
      } else {
        console.error('获取学校列表失败:', result.message);
      }
    } catch (error) {
      console.error('获取学校列表出错:', error);
    } finally {
      setLoadingSchools(false);
    }
  };

  // 添加团队成员
  const addMember = () => {
    const newMember = {
      name: '',
      schoolId: 1, // 默认选择北京邮电大学
      schoolName: '北京邮电大学',
      college: '',
      major: '',
      className: '',
      studentId: '',
      phone: '',
      introduction: '',
      isLeader: false
    };
    onChange([...data, newMember]);
  };

  // 删除团队成员
  const removeMember = (index) => {
    if (data.length > 1) {
      const newMembers = data.filter((_, i) => i !== index);
      onChange(newMembers);
    }
  };

  // 更新团队成员信息
  const updateMember = (index, field, value) => {
    const newMembers = [...data];
    newMembers[index] = {
      ...newMembers[index],
      [field]: value
    };
    onChange(newMembers);
  };

  // 更新学校选择
  const updateMemberSchool = (index, schoolId) => {
    const selectedSchool = schools.find(school => school.id === schoolId);
    if (selectedSchool) {
      const newMembers = [...data];
      newMembers[index] = {
        ...newMembers[index],
        schoolId: schoolId,
        schoolName: selectedSchool.name
      };
      onChange(newMembers);
    }
  };

  // 设置项目负责人
  const setLeader = (index) => {
    const newMembers = data.map((member, i) => ({
      ...member,
      isLeader: i === index
    }));
    onChange(newMembers);
  };

  // 验证学号格式（简单验证）
  const validateStudentId = (studentId) => {
    return /^\d{6,12}$/.test(studentId); // 8-12位数字
  };

  // 验证电话格式（简单验证）
  const validatePhone = (phone) => {
    return /^1[3-9]\d{9}$/.test(phone); // 11位手机号
  };

  const inputSx = {
    '& .MuiInputLabel-root': { color: '#94a3b8' },
    '& .MuiOutlinedInput-root': {
      color: '#e2e8f0',
      background: 'rgba(255, 255, 255, 0.02)',
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
      '&.Mui-focused fieldset': { borderColor: '#38bdf8' }
    },
    '& .MuiFormHelperText-root': { color: '#fca5a5' }
  };

  const selectSx = {
    color: '#e2e8f0',
    background: 'rgba(255, 255, 255, 0.02)',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.1)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#38bdf8' },
    '& .MuiSelect-icon': { color: '#94a3b8' }
  };

  return (
    <Paper 
      sx={{ 
        p: 4, 
        backgroundColor: 'rgba(30, 41, 59, 0.4)', 
        backdropFilter: 'blur(12px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        mb: 3 
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" sx={{ color: '#e2e8f0', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PersonIcon sx={{ color: '#38bdf8' }} />
          {t('yuyan_team_title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={addMember}
          sx={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.5)',
            '&:hover': {
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.5)'
            }
          }}
        >
          {t('yuyan_team_add')}
        </Button>
      </Box>

      <Box mb={3} sx={{ p: 2, borderRadius: '8px', background: 'rgba(56, 189, 248, 0.1)', border: '1px solid rgba(56, 189, 248, 0.2)' }}>
        <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
          {t('yuyan_team_hint')}
        </Typography>
      </Box>

      {data.map((member, index) => (
        <Card 
          key={index} 
          sx={{ 
            mb: 3, 
            backgroundColor: 'rgba(30, 41, 59, 0.6)',
            backdropFilter: 'blur(12px)',
            borderRadius: '12px',
            border: member.isLeader ? '1px solid #fbbf24' : '1px solid rgba(255, 255, 255, 0.08)',
            position: 'relative',
            overflow: 'visible'
          }}
        >
          {member.isLeader && (
            <Chip 
              icon={<StarIcon sx={{ fontSize: 16 }} />}
              label={t('yuyan_role_leader')} 
              size="small" 
              sx={{ 
                position: 'absolute', 
                top: -12, 
                left: 24, 
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', 
                color: '#fff',
                fontWeight: 600,
                boxShadow: '0 4px 6px -1px rgba(245, 158, 11, 0.4)',
                '& .MuiChip-icon': { color: '#fff' }
              }} 
            />
          )}
          
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} mt={member.isLeader ? 1 : 0}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: member.isLeader ? '#f59e0b' : '#64748b', width: 40, height: 40 }}>
                  {index + 1}
                </Avatar>
                <Typography variant="subtitle1" sx={{ color: '#e2e8f0', fontWeight: 600 }}>
                  {t('yuyan_team_member_info', { n: index + 1 })}
                </Typography>
              </Box>
              
              <Box>
                {!member.isLeader && (
                  <Button
                    size="small"
                    startIcon={<StarIcon />}
                    onClick={() => setLeader(index)}
                    sx={{ 
                      mr: 1, 
                      color: '#fbbf24',
                      borderColor: 'rgba(251, 191, 36, 0.3)',
                      '&:hover': { borderColor: '#fbbf24', background: 'rgba(251, 191, 36, 0.1)' }
                    }}
                    variant="outlined"
                  >
                    {t('yuyan_team_set_leader')}
                  </Button>
                )}
                
                {data.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => removeMember(index)}
                    sx={{ color: '#ef4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            </Box>

            <Grid container spacing={3}>
              {/* 第一行：学校选择 */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel sx={{ color: '#94a3b8' }}>{t('yuyan_team_school')}</InputLabel>
                  <Select
                    value={member.schoolId || member.school_id || 1}
                    label={t('yuyan_team_school')}
                    onChange={(e) => updateMemberSchool(index, e.target.value)}
                    disabled={loadingSchools}
                    sx={selectSx}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: '#1e293b',
                          color: '#e2e8f0',
                          '& .MuiMenuItem-root': {
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                            '&.Mui-selected': { bgcolor: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8' }
                          }
                        }
                      }
                    }}
                  >
                    {loadingSchools ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        {t('yuyan_team_loading')}
                      </MenuItem>
                    ) : (
                      schools.map((school) => (
                        <MenuItem key={school.id} value={school.id}>
                          {school.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>

              {/* 第二行：姓名、学院 */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('yuyan_team_name')}
                  value={member.name || ''}
                  onChange={(e) => updateMember(index, 'name', e.target.value)}
                  required
                  error={Boolean(member.name && member.name.trim() === '')}
                  helperText={member.name && member.name.trim() === '' ? t('yuyan_team_err_name') : ''}
                  sx={inputSx}
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: '#64748b', fontSize: 20 }} />
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('yuyan_team_college')}
                  value={member.college || ''}
                  onChange={(e) => updateMember(index, 'college', e.target.value)}
                  required
                  error={Boolean(member.college && member.college.trim() === '')}
                  helperText={member.college && member.college.trim() === '' ? t('yuyan_team_err_college') : ''}
                  sx={inputSx}
                  InputProps={{
                    startAdornment: <SchoolIcon sx={{ mr: 1, color: '#64748b', fontSize: 20 }} />
                  }}
                />
              </Grid>

              {/* 第三行：专业、班级 */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('yuyan_team_major')}
                  value={member.major || ''}
                  onChange={(e) => updateMember(index, 'major', e.target.value)}
                  required
                  error={Boolean(member.major && member.major.trim() === '')}
                  helperText={member.major && member.major.trim() === '' ? t('yuyan_team_err_major') : ''}
                  sx={inputSx}
                  InputProps={{
                    startAdornment: <SchoolIcon sx={{ mr: 1, color: '#64748b', fontSize: 20 }} />
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('yuyan_team_class')}
                  value={member.className || ''}
                  onChange={(e) => updateMember(index, 'className', e.target.value)}
                  required
                  error={Boolean(member.className && member.className.trim() === '')}
                  helperText={member.className && member.className.trim() === '' ? t('yuyan_team_err_class') : ''}
                  sx={inputSx}
                  InputProps={{
                    startAdornment: <ClassIcon sx={{ mr: 1, color: '#64748b', fontSize: 20 }} />
                  }}
                />
              </Grid>

              {/* 第四行：学号、电话 */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('yuyan_team_student_id')}
                  value={member.studentId || ''}
                  onChange={(e) => updateMember(index, 'studentId', e.target.value)}
                  required
                  error={Boolean(
                    (member.studentId && member.studentId.trim() === '') ||
                    (member.studentId && member.studentId.trim() !== '' && !validateStudentId(member.studentId))
                  )}
                  helperText={
                    member.studentId && member.studentId.trim() === ''
                      ? t('yuyan_team_err_student_id')
                      : member.studentId && member.studentId.trim() !== '' && !validateStudentId(member.studentId)
                      ? t('yuyan_team_err_student_id_format')
                      : ''
                  }
                  sx={inputSx}
                  InputProps={{
                    startAdornment: <BadgeIcon sx={{ mr: 1, color: '#64748b', fontSize: 20 }} />
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('yuyan_team_phone')}
                  value={member.phone || ''}
                  onChange={(e) => updateMember(index, 'phone', e.target.value)}
                  required
                  error={Boolean(
                    (member.phone && member.phone.trim() === '') ||
                    (member.phone && member.phone.trim() !== '' && !validatePhone(member.phone))
                  )}
                  helperText={
                    member.phone && member.phone.trim() === ''
                      ? t('yuyan_team_err_phone')
                      : member.phone && member.phone.trim() !== '' && !validatePhone(member.phone)
                      ? t('yuyan_team_err_phone_format')
                      : ''
                  }
                  sx={inputSx}
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ mr: 1, color: '#64748b', fontSize: 20 }} />
                  }}
                />
              </Grid>

              {/* 第五行：成员介绍 */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('yuyan_team_intro')}
                  value={member.introduction || ''}
                  onChange={(e) => updateMember(index, 'introduction', e.target.value)}
                  multiline
                  rows={1}
                  placeholder={t('yuyan_team_intro_placeholder')}
                  sx={inputSx}
                  InputProps={{
                    startAdornment: <AccountBoxIcon sx={{ mr: 1, color: '#64748b', fontSize: 20, mt: 1, alignSelf: 'flex-start' }} />
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      {data.length === 0 && (
        <Box 
          textAlign="center" 
          py={6}
          sx={{ 
            border: '2px dashed rgba(255, 255, 255, 0.1)',
            borderRadius: 4,
            backgroundColor: 'rgba(255, 255, 255, 0.02)'
          }}
        >
          <Typography variant="body1" sx={{ color: '#94a3b8', mb: 2 }}>
            {t('yuyan_team_empty')}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addMember}
            sx={{
              borderColor: '#38bdf8',
              color: '#38bdf8',
              '&:hover': { borderColor: '#0ea5e9', backgroundColor: 'rgba(56, 189, 248, 0.1)' }
            }}
          >
            {t('yuyan_team_add_first')}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default TeamMembersForm;